import * as React from "react";
import { Typography, Box, Collapse, Avatar, Chip } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  spacing,
  colors,
  typography,
} from "../../../../../../../../../../../../assets/styles/tokens";
import IconButton from "@mui/material/IconButton";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import { Stack } from "@mui/system";

const SERIES = [
  { id: "qftd_ftd", label: "QFTD/FTD", dataKey: "qftd_ftd", color: "#F59E0B" },
  { id: "ftd_regs", label: "FTD/Regs", dataKey: "ftd_regs", color: "#3B82F6" },
  { id: "out_in", label: "Out/In", dataKey: "out_in", color: "#8B5CF6" },
  { id: "regs_out", label: "Regs/Out", dataKey: "regs_out", color: "#F87171" },
];

const chartData = [
  { x: "08 Oct", qftd_ftd: 118, ftd_regs: 102, out_in: 60, regs_out: 32 },
  { x: "11 Oct", qftd_ftd: 119, ftd_regs: 100, out_in: 63, regs_out: 44 },
  { x: "14 Oct", qftd_ftd: 116, ftd_regs: 92, out_in: 68, regs_out: 27 },
  { x: "17 Oct", qftd_ftd: 117, ftd_regs: 96, out_in: 65, regs_out: 35 },
  { x: "20 Oct", qftd_ftd: 114, ftd_regs: 85, out_in: 73, regs_out: 32 },
  { x: "23 Oct", qftd_ftd: 116, ftd_regs: 93, out_in: 67, regs_out: 38 },
  { x: "26 Oct", qftd_ftd: 118, ftd_regs: 100, out_in: 62, regs_out: 40 },
  { x: "29 Oct", qftd_ftd: 115, ftd_regs: 82, out_in: 75, regs_out: 30 },
  { x: "01 Nov", qftd_ftd: 120, ftd_regs: 104, out_in: 60, regs_out: 41 },
  { x: "04 Nov", qftd_ftd: 116, ftd_regs: 89, out_in: 72, regs_out: 42 },
];

const notesData: Record<number, { title: string; fullText: string }> = {
  2: {
    title:
      "Bing - Bingo main 2 tcpa & bingo main 2[bingo] tier3 tcpa & bingo uk bonus tcp - Into one ad group",
    fullText:
      "This is the complete note text for Oct 14. Full description including campaign details, targeting, ad group adjustments, or any additional commentary.",
  },
  4: {
    title:
      "Google - Main campaign tier2 tcpa adjustments & budget reallocation",
    fullText:
      "This is the complete note text for Oct 20. Full description including targeting changes, bid strategy updates, and performance notes.",
  },
  8: {
    title: "Facebook - Retargeting campaign adjustments for Nov 01",
    fullText:
      "This is the complete note text for Nov 01. Full description including audience changes, creative updates, and budget shifts.",
  },
};

const notes = Object.keys(notesData).map(Number);

// Custom dot — hollow normally, filled + enlarged when active
const CustomDot = (props: any) => {
  const { cx, cy, activeIndex, index, color, dataKey, onDotClick } = props;
  if (cx === undefined || cy === undefined) return null;
  const isActive = activeIndex === index;

  return (
    <g
      style={{ cursor: "pointer" }}
      onClick={(e) => {
        e.stopPropagation();
        onDotClick(dataKey, index);
      }}
    >
      <circle cx={cx} cy={cy} r={10} fill="transparent" />
      <circle
        cx={cx}
        cy={cy}
        r={isActive ? 5 : 3}
        fill={isActive ? color : "white"}
        stroke={isActive ? "white" : color}
        strokeWidth={1}
      />
    </g>
  );
};

const CustomTooltip = ({ active, payload, activeSeries }: any) => {
  if (!active || !payload) return null;

  return (
    <Box
      sx={{
        borderRadius: "8px",
        background: colors.Base.White.$value,
        border: "none",
        boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
        fontSize: typography.Size.Text.xs.$value,
        padding: "8px",
      }}
    >
      {payload.map((item: any) => {
        const isActive = item.dataKey === activeSeries;
        return (
          <Typography
            key={item.dataKey}
            sx={{
              fontSize: typography.Size.Text.xs.$value,
              fontWeight: isActive ? 700 : 400,
              lineHeight: typography.Line_height.text.sm.$value,
              color: isActive ? item.color : colors.GrayLightMode[600].$value,
            }}
          >
            {item.name}: {item.value}
          </Typography>
        );
      })}
    </Box>
  );
};

// Custom X axis tick — bold when active
const CustomXTick = (props: any) => {
  const { x, y, payload, index, activeIndex, activeNote } = props;
  const [day, month] = payload.value.split(" ");
  const isActiveNote = activeNote === index;
  const isActive = activeIndex === index;

  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      fill={
        isActiveNote || isActive
          ? colors.GrayLightMode[900].$value
          : colors.GrayLightMode[600].$value
      }
      fontSize={typography.Size.Text.xxs.$value}
      fontWeight={
        isActive || isActiveNote
          ? typography.Font_weight.Bold.$value
          : typography.Font_weight.Medium.$value
      }
    >
      <tspan x={x} dy="1em">
        {day}
      </tspan>
      <tspan x={x} dy="1.5em">
        {month}
      </tspan>
    </text>
  );
};

export default function FunnelRatioMultiChart() {
  const chartRef = React.useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = React.useState(0);
  const [activeNote, setActiveNote] = React.useState<number | null>(null);
  const [expandedNotes, setExpandedNotes] = React.useState<Set<number>>(
    new Set(),
  );
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const [activeSeries, setActiveSeries] = React.useState<string | null>(null);

  React.useLayoutEffect(() => {
    if (!chartRef.current) return;
    const resize = new ResizeObserver((entries) => {
      setChartWidth(entries[0].contentRect.width);
    });
    resize.observe(chartRef.current);
    return () => resize.disconnect();
  }, []);

  const toggleFullNote = (index: number) => {
    setExpandedNotes((prev) => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  };

  const handleChartClick = (rechartsData: any) => {
    if (!rechartsData || rechartsData.activeTooltipIndex === undefined) {
      setActiveIndex(null);
      setActiveNote(null);
      return;
    }
    const index = rechartsData.activeTooltipIndex;
    setActiveIndex((prev) => (prev === index ? null : index));

    if (notes.includes(index)) {
      setActiveNote((prev) => (prev === index ? null : index));
    } else {
      setActiveNote(null);
    }
  };

  // const handleNoteClick = (index: number) => {
  //   setActiveNote((prev) => (prev === index ? null : index));
  // };

  const getNoteLeft = (index: number) => {
    const marginLeft = 40;
    const marginRight = 24;
    const usableWidth = chartWidth - marginLeft - marginRight;
    const step = usableWidth / (chartData.length - 1);
    return marginLeft + step * index;
  };

  return (
    <Box onClick={() => setActiveNote(null)}>
      {/* Chart */}
      <Box ref={chartRef} sx={{ width: "100%", position: "relative" }}>
        <ResponsiveContainer width="100%" height={265}>
          <LineChart
            data={chartData}
            margin={{ top: 16, right: 24, left: 0, bottom: 48 }}
            onClick={(data, event) => {
              event?.stopPropagation();
              handleChartClick(data);
            }}
            style={{ cursor: "pointer" }}
          >
            <Legend
              verticalAlign="top"
              iconType="circle"
              iconSize={8}
              wrapperStyle={{
                fontSize: typography.Size.Text.xxs.$value,
                paddingLeft: 10,
                paddingBottom: 20,
              }}
              formatter={(value, entry: any) => {
                const isActive = activeSeries === entry.dataKey;
                return (
                  <span
                    style={{
                      color: isActive
                        ? colors.Base.White.$value
                        : colors.GrayLightMode[600].$value,
                      background: isActive ? entry.color : "transparent",
                      padding: "2px 3px",
                      borderRadius: spacing.Radius.xs.$value,
                      lineHeight: "10px",
                      transition: "all 0.2s ease",
                      fontWeight: isActive
                        ? typography.Font_weight.Semibold.$value
                        : typography.Font_weight.Medium.$value,
                    }}
                  >
                    {value}
                  </span>
                );
              }}
              onClick={(e: any) =>
                setActiveSeries((prev) =>
                  prev === e.dataKey ? null : e.dataKey,
                )
              }
            />

            <CartesianGrid
              strokeDasharray=""
              vertical={false}
              stroke={colors.GrayLightMode[300].$value}
              opacity={0.3}
            />

            <XAxis
              dataKey="x"
              tick={(tickProps: any) => (
                <CustomXTick
                  {...tickProps}
                  activeNote={activeNote}
                  activeIndex={activeIndex}
                />
              )}
              tickLine={false}
              axisLine={false}
              interval={0}
              tickMargin={10}
            />

            <YAxis
              domain={[20, 120]}
              tickCount={6}
              tick={{
                fill: colors.GrayLightMode[600].$value,
                fontSize: typography.Size.Text.xxs.$value,
                fontWeight: typography.Font_weight.Medium.$value,
              }}
              tickLine={false}
              axisLine={false}
              width={40}
              tickMargin={3}
            />

            <Tooltip
              cursor={{
                stroke: colors.GrayLightMode[200].$value,
                strokeWidth: 1,
              }}
              content={(props) => (
                <CustomTooltip {...props} activeSeries={activeSeries} />
              )}
            />

            {SERIES.map((s) => (
              <Line
                key={s.id}
                type="monotone"
                dataKey={s.dataKey}
                name={s.label}
                stroke={s.color}
                strokeWidth={
                  activeSeries === s.dataKey || activeSeries === null ? 3 : 2
                }
                opacity={
                  activeSeries === null || activeSeries === s.dataKey ? 1 : 0.2
                }
                dot={(dotProps: any) => (
                  <CustomDot
                    {...dotProps}
                    activeIndex={activeIndex}
                    color={s.color}
                    dataKey={s.dataKey}
                    onDotClick={(dataKey: string, index: number) => {
                      setActiveSeries(dataKey);
                      setActiveIndex(index);
                      if (notes.includes(index)) {
                        setActiveNote(index); // ← always OPEN, never toggle/close
                      } else {
                        setActiveNote(null);
                      }
                    }}
                  />
                )}
                activeDot={false}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>

        {/* Note Icons */}
        {notes.map((index) => (
          <IconButton
            key={index}
            size="small"
            sx={{
              position: "absolute",
              bottom: 4,
              left: getNoteLeft(index),
              transform: "translateX(-50%)",
            }}
            onClick={(e) => {
              e.stopPropagation();
              // handleNoteClick(index);
            }}
            disableRipple
          >
            {activeNote === index ? (
              <StickyNote2OutlinedIcon
                sx={{ width: "14px", color: colors.GrayLightMode[900].$value }}
              />
            ) : (
              <StickyNote2OutlinedIcon
                sx={{ width: "14px", fill: colors.GrayLightMode[600].$value }}
              />
            )}
          </IconButton>
        ))}
      </Box>

      {/* Collapse note panel */}
      <Collapse in={activeNote !== null}>
        {activeNote !== null && notesData[activeNote] && (
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              display: "flex",
              px: 1.5,
              py: 1,
              gap: 0.5,
              alignItems: "flex-start",
              flexDirection: "column",
            }}
          >
            <Stack flexDirection="row" gap={0.5} alignItems="center">
              <Avatar sx={{ width: 18, height: 18 }} src="#" />
              <Typography
                fontSize={typography.Size.Text.xs.$value}
                lineHeight={typography.Line_height.text.xs.$value}
                fontWeight={typography.Font_weight.Regular.$value}
                color={colors.GrayLightMode[700].$value}
              >
                Netanel Dahan
              </Typography>
              <Chip
                label="Product & Services"
                variant="outlined"
                size="small"
                sx={{
                  height: spacing.Spacing["1xl"].$value,
                  fontSize: typography.Size.Text.xxs.$value,
                  lineHeight: typography.Line_height.text.xxs.$value,
                  fontWeight: typography.Font_weight.Semibold.$value,
                  color: colors.Brand[700].$value,
                  borderColor: colors.Brand[700].$value,
                  borderRadius: spacing.Radius["2xl"].$value,
                  ml: 0.5,
                }}
              />
            </Stack>

            <Box>
              <Typography
                fontSize={typography.Size.Text.xs.$value}
                color={colors.GrayLightMode[900].$value}
                fontWeight={typography.Font_weight.Semibold.$value}
              >
                {notesData[activeNote].title}
              </Typography>

              <Collapse in={expandedNotes.has(activeNote)}>
                <Typography
                  fontSize={typography.Size.Text.xs.$value}
                  color={colors.GrayLightMode[900].$value}
                  fontWeight={typography.Font_weight.Semibold.$value}
                  mt={0.5}
                >
                  {notesData[activeNote].fullText}
                </Typography>
              </Collapse>

              <Typography
                fontSize={typography.Size.Text.xs.$value}
                fontWeight={typography.Font_weight.Medium.$value}
                color={colors.Brand[700].$value}
                mt={0.5}
                sx={{ cursor: "pointer", textDecoration: "underline" }}
                onClick={() => toggleFullNote(activeNote)}
              >
                {expandedNotes.has(activeNote)
                  ? "Hide full Note"
                  : "See full Note"}
              </Typography>
            </Box>
          </Box>
        )}
      </Collapse>
    </Box>
  );
}
